import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiKey, FiEye, FiEyeOff } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const CaptainManagement = () => {
  const [captains, setCaptains] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedCaptain, setSelectedCaptain] = useState(null);
  const [captainForm, setCaptainForm] = useState({
    password: '',
    name: '',
    contactNumber: '',
    assignedTables: [],
    isActive: true
  });
  const [generatedUsername, setGeneratedUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-generate username when name changes
  useEffect(() => {
    const fetchNextUsername = async () => {
      if (captainForm.name && !selectedCaptain) {
        try {
          const response = await api.get('/vendor/captains/next-username', {
            params: { name: captainForm.name }
          });
          if (response.data.success) {
            setGeneratedUsername(response.data.data.username);
          }
        } catch (error) {
          // Fallback to local generation if API fails
          const namePart = captainForm.name.toLowerCase().replace(/\s+/g, '-').substring(0, 15);
          setGeneratedUsername(`cap-1-${namePart}`);
        }
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      fetchNextUsername();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [captainForm.name, selectedCaptain]);

  const fetchData = async () => {
    try {
      const [captainsRes, tablesRes] = await Promise.all([
        api.get('/vendor/captains'),
        api.get('/vendor/tables')
      ]);
      setCaptains(captainsRes.data.data || []);
      setTables(tablesRes.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCaptain) {
        await api.put(`/vendor/captains/${selectedCaptain._id}`, captainForm);
        toast.success('Captain updated successfully');
      } else {
        await api.post('/vendor/captains', captainForm);
        toast.success('Captain created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save captain');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/vendor/captains/${selectedCaptain._id}/reset-password`, {
        newPassword
      });
      toast.success('Password reset successfully');
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedCaptain(null);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const openModal = (captain = null) => {
    if (captain) {
      setSelectedCaptain(captain);
      setCaptainForm({
        password: '',
        name: captain.name,
        contactNumber: captain.contactNumber,
        assignedTables: captain.assignedTables?.map(t => t._id) || [],
        isActive: captain.isActive
      });
      setGeneratedUsername(captain.username);
    } else {
      // Reset form for new captain
      const emptyForm = {
        password: '',
        name: '',
        contactNumber: '',
        assignedTables: [],
        isActive: true
      };
      setCaptainForm(emptyForm);
      setGeneratedUsername('');
      setSelectedCaptain(null);
    }
    setShowModal(true);
  };

  const openPasswordModal = (captain) => {
    setSelectedCaptain(captain);
    setShowPasswordModal(true);
  };

  const resetForm = () => {
    setCaptainForm({
      password: '',
      name: '',
      contactNumber: '',
      assignedTables: [],
      isActive: true
    });
    setSelectedCaptain(null);
  };

  const toggleTableAssignment = (tableId) => {
    const currentTables = captainForm.assignedTables;
    if (currentTables.includes(tableId)) {
      setCaptainForm({
        ...captainForm,
        assignedTables: currentTables.filter(id => id !== tableId)
      });
    } else {
      setCaptainForm({
        ...captainForm,
        assignedTables: [...currentTables, tableId]
      });
    }
  };

  const selectAllTables = () => {
    const allTableIds = tables.map(table => table._id);
    setCaptainForm({
      ...captainForm,
      assignedTables: allTableIds
    });
  };

  const clearAllTables = () => {
    setCaptainForm({
      ...captainForm,
      assignedTables: []
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-3 sm:p-4 md:p-6">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Captain Management</h1>
            <p className="text-gray-600 text-sm sm:text-base">Manage your restaurant captains and table assignments</p>
          </div>
          <button
            className="px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
            onClick={() => openModal()}
          >
            <FiPlus className="text-sm sm:text-base" />
            <span className="hidden sm:inline">Add Captain</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Captains Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-purple-50 to-purple-100 border-b border-gray-200">
              <tr>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Name</th>
                <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Username</th>
                <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Contact</th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tables</th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {captains.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((captain) => (
                <tr key={captain._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <div className="text-sm font-medium text-gray-900">{captain.name}</div>
                    <div className="sm:hidden text-xs text-gray-500 mt-1">{captain.username}</div>
                    <div className="sm:hidden text-xs text-gray-500">{captain.contactNumber}</div>
                  </td>
                  <td className="hidden sm:table-cell px-6 py-4 text-sm text-gray-700">{captain.username}</td>
                  <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-700">{captain.contactNumber}</td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    {captain.assignedTables?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {captain.assignedTables.slice(0, 3).map(table => (
                          <span key={table._id} className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            T{table.tableNumber}
                          </span>
                        ))}
                        {captain.assignedTables.length > 3 && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            +{captain.assignedTables.length - 3}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs sm:text-sm">No tables</span>
                    )}
                  </td>
                  <td className="px-3 sm:px-6 py-3 sm:py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${captain.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                      }`}>
                      {captain.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                 <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <div className="flex items-center gap-1 sm:gap-2">
                    
                    <button
                      className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:scale-110 flex items-center justify-center"
                      onClick={() => openModal(captain)}
                      title="Edit Captain"
                    >
                      <FiEdit className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                    </button>

                    <button
                      className="p-1.5 sm:p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200 hover:scale-110 flex items-center justify-center"
                      onClick={() => openPasswordModal(captain)}
                      title="Reset Password"
                    >
                      <FiKey className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                    </button>

                  </div>
                </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {captains.length === 0 && (
          <div className="bg-gray-50 rounded-lg p-6 sm:p-12 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiPlus className="text-gray-400 text-2xl sm:text-3xl" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No Captains Found</h3>
            <p className="text-gray-600 text-sm sm:text-base mb-6">Start by adding your first captain to manage table operations.</p>
            <button
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center mx-auto gap-2 text-sm sm:text-base"
              onClick={() => openModal()}
            >
              <FiPlus className="text-sm sm:text-base" />
              <span className="hidden sm:inline">Add Your First Captain</span>
              <span className="sm:hidden">Add First Captain</span>
            </button>
          </div>
        )}

        {/* Pagination */}
        {Math.ceil(captains.length / itemsPerPage) > 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6 mt-6">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, captains.length)}-{Math.min(currentPage * itemsPerPage, captains.length)} of {captains.length} captains
              </div>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
                <div className="flex gap-2 items-center justify-center">
                  <button
                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-xs sm:text-sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">‹</span>
                  </button>
                  {[...Array(Math.ceil(captains.length / itemsPerPage))].map((_, idx) => {
                    const pageNum = idx + 1;
                    const totalCaptainPages = Math.ceil(captains.length / itemsPerPage);
                    if (
                      pageNum === 1 ||
                      pageNum === totalCaptainPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          className={`min-w-[28px] sm:min-w-[36px] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${pageNum === currentPage
                              ? 'bg-purple-500 text-white shadow-lg'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={pageNum} className="px-1 sm:px-2 text-gray-400 text-xs sm:text-sm">...</span>;
                    }
                    return null;
                  })}
                  <button
                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-xs sm:text-sm"
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(captains.length / itemsPerPage), prev + 1))}
                    disabled={currentPage === Math.ceil(captains.length / itemsPerPage)}
                  >
                    <span className="hidden sm:inline">Next</span>
                    <span className="sm:hidden">›</span>
                  </button>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm justify-center">
                  <span className="text-gray-600">Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 sm:px-3 py-1 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none transition-colors duration-200 text-xs sm:text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-gray-600">per page</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Captain Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >

            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 sm:p-6 flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-bold">
                {selectedCaptain ? "Edit Captain" : "Add Captain"}
              </h2>

              <button
                className="text-white hover:bg-white/20 rounded-lg p-2"
                onClick={() => setShowModal(false)}
              >
                <FiPlus className="text-lg sm:text-xl rotate-45" />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col flex-1 overflow-hidden"
            >

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm sm:text-base"
                      placeholder="Enter captain name"
                      value={captainForm.name}
                      onChange={(e) =>
                        setCaptainForm({ ...captainForm, name: e.target.value })
                      }
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Number *
                    </label>

                    <input
                      type="text"
                      name="captain_phone_input"
                      autoComplete="off"
                      inputMode="numeric"
                      readOnly
                      onFocus={(e) => e.target.removeAttribute("readonly")}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm sm:text-base"
                      placeholder="Enter 10-digit contact number"
                      value={captainForm.contactNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setCaptainForm({
                            ...captainForm,
                            contactNumber: value,
                          });
                        }
                      }}
                      pattern="[0-9]{10}"
                      maxLength="10"
                      required
                    />
                  </div>
                </div>

                {/* Username */}
                {generatedUsername && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username (Auto-generated)
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg bg-blue-50 text-blue-700 font-medium text-sm sm:text-base"
                      value={generatedUsername}
                      readOnly
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      ✓ Username will be automatically assigned based on name
                    </p>
                  </div>
                )}

                {/* Password */}
                {!selectedCaptain && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>

                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="w-full px-3 sm:px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm sm:text-base"
                        value={captainForm.password}
                        onChange={(e) =>
                          setCaptainForm({
                            ...captainForm,
                            password: e.target.value,
                          })
                        }
                        placeholder="Enter password"
                        required
                      />

                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <FiEyeOff size={16} className="sm:size-18" />
                        ) : (
                          <FiEye size={16} className="sm:size-18" />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Tables */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Assign Tables
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs px-3 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-md transition-colors duration-200"
                        onClick={selectAllTables}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors duration-200"
                        onClick={clearAllTables}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">

                    {tables.map((table) => (
                      <label
                        key={table._id}
                        className={`flex items-center gap-2 p-2 border-2 rounded-lg cursor-pointer transition
                  ${captainForm.assignedTables.includes(table._id)
                            ? "border-purple-500 bg-purple-50"
                            : "border-gray-300 hover:border-purple-300"
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={captainForm.assignedTables.includes(table._id)}
                          onChange={() => toggleTableAssignment(table._id)}
                          className="text-purple-600"
                        />

                        <span className="text-sm font-medium">
                          Table {table.tableNumber}
                        </span>
                      </label>
                    ))}

                  </div>

                  {tables.length === 0 && (
                    <p className="text-gray-400 text-sm mt-2">
                      No tables available. Generate QR codes first.
                    </p>
                  )}

                  {tables.length > 0 && (
                    <p className="text-xs text-gray-600 mt-2">
                      {captainForm.assignedTables.length} of {tables.length} tables selected
                    </p>
                  )}
                </div>

                {/* Active Toggle */}
                {selectedCaptain && (
                  <div className="mb-6">
                    <label className="flex items-center gap-3 cursor-pointer p-4 bg-gray-50 rounded-lg border">
                      <input
                        type="checkbox"
                        checked={captainForm.isActive}
                        onChange={(e) =>
                          setCaptainForm({
                            ...captainForm,
                            isActive: e.target.checked,
                          })
                        }
                        className="w-5 h-5 text-purple-600"
                      />

                      <span
                        className={`font-medium ${captainForm.isActive
                            ? "text-green-600"
                            : "text-red-600"
                          }`}
                      >
                        {captainForm.isActive
                          ? "✓ Captain is Active"
                          : "✗ Captain is Inactive"}
                      </span>
                    </label>

                    <p className="text-xs text-gray-600 mt-2">
                      {captainForm.isActive
                        ? "Captain can login and manage orders"
                        : "Captain cannot login or access the system"}
                    </p>
                  </div>
                )}

              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0">

                <button
                  type="button"
                  className="px-4 sm:px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm sm:text-base"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-4 sm:px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm sm:text-base"
                >
                  {selectedCaptain ? "Update" : "Create"} Captain
                </button>

              </div>

            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedCaptain && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-bold">Reset Password - {selectedCaptain.name}</h2>
                <button
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowPasswordModal(false)}
                >
                  <FiPlus className="text-lg sm:text-xl rotate-45" />
                </button>
              </div>
            </div>
            <form onSubmit={handleResetPassword} autoComplete='off'>
              <div className="p-4 sm:p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Password *</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      className="w-full px-3 sm:px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors duration-200 text-sm sm:text-base"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <FiEyeOff size={16} className="sm:size-18" /> : <FiEye size={16} className="sm:size-18" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
                >
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaptainManagement;
